"use client";

import { useEffect } from "react";
import { useAuth } from "@/components/auth-provider";
import {
    captureTrackingFromUrl,
    captureYmClientId,
    getStoredYclid,
    getStoredYmClientId,
} from "@/lib/ym";
import { api } from "@/lib/api";

const PUSH_FLAG = "ym_pushed_track";

/** Client-side bootstrap for Yandex Direct attribution.
 *
 *  Runs on every page mount: parses `?yclid=…` from the URL, fetches
 *  the Y.Metrika ClientID, and stashes both in localStorage so the
 *  signup flow / bot deep-links can forward them.
 *
 *  Once the user is authenticated, we POST whatever we have to
 *  `/user/track` exactly once per session so the backend can attribute
 *  YK / Stars payments back to the original ad click. */
export function TrackingInit() {
    const { user } = useAuth();

    useEffect(() => {
        captureTrackingFromUrl();
        // Best-effort — Y.Metrika isn't always loaded yet on the very
        // first paint; the helper retries via promise resolution.
        void captureYmClientId();
    }, []);

    useEffect(() => {
        if (!user) return;
        if (typeof window === "undefined") return;
        try {
            if (window.sessionStorage.getItem(PUSH_FLAG)) return;
        } catch {
            // sessionStorage may be disabled — fall through, worst case
            // we re-POST on every mount which is a noop on the backend
            // because of COALESCE.
        }
        const ym_client_id = getStoredYmClientId();
        const yclid = getStoredYclid();
        if (!ym_client_id && !yclid) return;
        api
            .post("/user/track", { ym_client_id, yclid })
            .then(() => {
                try {
                    window.sessionStorage.setItem(PUSH_FLAG, "1");
                } catch {
                    // ignore
                }
            })
            .catch(() => {
                // Tracking is best-effort — never block the user on a
                // failed POST.
            });
    }, [user]);

    return null;
}
